import React from "react";
import { AGENT, ConfidencePill, DataState, IcArrow, IcSpark, IcBack, IcBath, IcBed, IcCheck, IcClock, IcDoc, IcEdit, IcExternal, IcRefresh, IcSearch, IcSend, IcShield, IcSq, IcX, LISTING_AGENT, RECEIVING_BROKERAGE, StatusPill, TRANSFERRING_BROKERAGE, listingFormFor } from "./shared.jsx";
import { api } from "../lib/api.js";
import { FooterNav, StepHeader } from "./steps_early.jsx";
const { useState: _uS, useEffect: _uE, useRef: _uR } = React;

// ============ LISTING TRANSFERS ============
// MLS feeds (ARMLS via Spark, MLSSAZ/Tucson via Spark) are configured at the
// org level — see backend/README.md and admin/MLS Settings.html. From the
// agent's POV the listings are simply already there, pulled by member ID.

function StepListings({ onNext, onBack, listings, setListings, agent, openSign, loading, error, onReload, provenance, associations }) {
  // Primary association code from the agent's board selection (agreements step).
  // Falls back to "WeSERV" — the correct default for this brokerage.
  const primaryAssoc = associations?.assoc?.[0] || "weserv";
  const [query, setQuery] = _uS("");
  const [selected, setSelected] = _uS(null);
  const [scanning, setScanning] = _uS(false);
  const [lastScan, setLastScan] = _uS("just now");

  const visible = listings.filter(l =>
    !query.trim() || l.addr.toLowerCase().includes(query.toLowerCase()) ||
    l.mls.includes(query) || (l.seller.name || "").toLowerCase().includes(query.toLowerCase())
  );

  const allTransferred = listings.length > 0 && listings.every(l => l.transferStatus === "transferred");
  const anyInFlight = listings.some(l => ["sent_owner", "sent_broker"].includes(l.transferStatus));

  // Re-scan re-hits GET /v1/listings/mine?live=1 via the App-provided reloader.
  async function rescan() {
    setScanning(true);
    try { await onReload?.(); setLastScan("just now"); }
    finally { setScanning(false); }
  }

  return (
    <div style={{maxWidth: 1100}}>
      <StepHeader eyebrow="Step 06 / 09" title="Transfer your active listings."
        subtitle={`We pull every active listing where you're the listing or co-list agent from ARMLS (member #${agent.shortId || "—"}). On transfer, George Laughton is the listing agent of record and you're the co-list agent. Sellers are matched to Followup Boss contacts — review each, then send for broker and owner signature.`}/>

      <DataState loading={loading} error={error} onReload={onReload} noun="listings"/>

      {/* Provenance — show ONLY for live (real pull) or demo (genuine no-feed
          fallback). On error the DataState above shows the retry; we suppress the
          sample-data note so a failed pull never reads as "showing sample listings." */}
      {provenance && !loading && (provenance.mode === "live" || provenance.mode === "demo") && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, fontSize: 13,
          color: provenance.mode === "live" ? "var(--ep-down-to-earth)" : "var(--ep-fg-muted)" }}>
          <IcSpark size={13}/>
          {provenance.mode === "live"
            ? `${provenance.count} active listing${provenance.count === 1 ? "" : "s"} from ${String(provenance.feed || "ARMLS").toUpperCase()} · Spark API`
            : "Showing sample listings (demo) — your live MLS pull activates once your board feed is connected."}
        </div>
      )}

      <>
      {/* Search & scan bar */}
      <div style={{display: "flex", gap: 14, alignItems: "center", marginBottom: 24, padding: "16px 20px", background: "var(--ep-bg-elevated)", border: "1px solid var(--ep-border)", borderRadius: 2}}>
        <IcSearch size={18}/>
        <input className="ep-input" style={{border: 0, background: "transparent", padding: 0, fontSize: 15}}
          placeholder="Search MLS ID, address, or seller"
          value={query} onChange={e => setQuery(e.target.value)}/>
        <div style={{display: "flex", alignItems: "center", gap: 10, color: "var(--ep-fg-muted)", fontSize: 13}}>
          <IcClock size={14}/> Auto-synced from ARMLS
        </div>
        {/* Listings autoload on arrival (App fetches by the stored MlsId — the agent
            never types it). This is only a SECONDARY manual refresh. */}
        <button className="ep-btn ghost sm" onClick={rescan} disabled={scanning}>
          <IcRefresh size={12}/> {scanning ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Counts */}
      <div className="ep-strip-mobile" style={{display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 0, marginBottom: 32, background: "var(--ep-border)", border: "1px solid var(--ep-border)"}}>
        {[
          {n: listings.length, l: "Active Listings"},
          {n: listings.filter(l => l.transferStatus === "draft").length, l: "Drafts"},
          {n: listings.filter(l => ["sent_owner","sent_broker"].includes(l.transferStatus)).length, l: "In Signature"},
          {n: listings.filter(l => l.transferStatus === "transferred").length, l: "Transferred"},
        ].map((s,i) => (
          <div key={i} style={{background: "var(--ep-bg-elevated)", padding: "20px 22px"}}>
            <div style={{font: "300 42px/1 var(--ep-font-display)", color: "var(--ep-origin)"}}>{s.n}</div>
            <div className="ep-caption" style={{marginTop: 6}}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Listings grid */}
      <div className="vstack" style={{gap: 14, marginBottom: 40}}>
        {visible.map(l => {
          const listing = l.assoc ? l : {...l, assoc: primaryAssoc};
          return (
            <ListingRow key={l.id} listing={listing}
              onEdit={() => setSelected(l.id)}
              onSend={() => sendForSig(l.id, setListings, openSign)}/>
          );
        })}
        {visible.length === 0 && (
          <div style={{padding: 40, textAlign: "center", color: "var(--ep-fg-muted)", border: "1px dashed var(--ep-border)", background: "var(--ep-bg-elevated)"}}>
            No listings match that search.
          </div>
        )}
      </div>

      {selected && (
        <ListingDetailModal
          listing={{...(listings.find(l => l.id === selected) || {}), assoc: (listings.find(l => l.id === selected) || {}).assoc || primaryAssoc}}
          agent={agent}
          onClose={() => setSelected(null)}
          onSave={(updated) => { setListings(listings.map(x => x.id === updated.id ? updated : x)); setSelected(null); }}
          onSend={async (updated) => {
            // Save edits (including any typed email) to listings state BEFORE closing,
            // so they're preserved if the send fails.
            setListings(listings.map(x => x.id === updated.id ? {...updated, transferStatus: "sending"} : x));
            setSelected(null);
            try {
              // Map UI assoc codes ("phx", "weserv") to BoldSign registry keys ("PAR", "WeSERV").
              const ASSOC_KEYS = { weserv:"WeSERV", saar:"SAAR", phx:"PAR", tar:"TAR",
                naar:"NAAR", paar:"PAAR", wmar:"WMAR" };
              const assocKey = ASSOC_KEYS[updated.assoc || primaryAssoc] || "WeSERV";
              const res = await fetch("/api/v1/listings/transfer", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ mlsId: updated.mls, association: assocKey, listing: updated }),
              }).then(r => r.json());
              if (res.ok) {
                setListings(curr => curr.map(x => x.id === updated.id ? {...x, transferStatus: "sent_broker", envelopeId: res.envelopeId} : x));
              } else {
                // Revert to draft but keep edits (seller email etc.) intact.
                setListings(curr => curr.map(x => x.id === updated.id ? {...updated, transferStatus: "draft"} : x));
                alert("Transfer failed: " + (res.message || res.error || res.publicMessage || JSON.stringify(res)));
              }
            } catch (err) {
              setListings(curr => curr.map(x => x.id === updated.id ? {...updated, transferStatus: "draft"} : x));
              alert("Transfer failed: " + err.message);
            }
          }}/>
      )}
      </>

      <FooterNav onBack={onBack} onNext={onNext}
        nextDisabled={!allTransferred && !window.__allowSkipTransfers}
        nextLabel={allTransferred ? "Continue to Escrow Transfers"
          : (anyInFlight ? "Waiting on signatures…" : "Send all for signature first")}/>

      {!allTransferred && (
        <div style={{textAlign: "right", marginTop: 12}}>
          <span className="ep-link" style={{fontSize: 12, color: "var(--ep-fg-muted)"}}
            onClick={() => { window.__allowSkipTransfers = true; onNext(); }}>
            Skip for now (handle transfers later)
          </span>
        </div>
      )}
    </div>
  );
}

function sendForSig(id, setListings, openSign) {
  setListings(curr => curr.map(x => x.id === id ? {...x, transferStatus: "sent_broker"} : x));
  const l = (window.__listings || []).find(x => x.id === id);
  const f = listingFormFor(l?.assoc);
  openSign({k: `transfer_${id}`, title: `Listing Transfer — ${l?.addr || ""}`, sub: `${f.short} · ${f.formTitle}`, pages: 1,
    listingForm: f, listingMeta: {addr: l?.addr, city: l?.city, mls: l?.mls, seller: l?.seller?.name}}, false, () => {
    setListings(curr => curr.map(x => x.id === id ? {...x, transferStatus: "signed"} : x));
    setTimeout(() => setListings(curr => curr.map(x => x.id === id ? {...x, transferStatus: "transferred"} : x)), 1400);
  });
}

function ListingRow({ listing, onEdit, onSend }) {
  const l = listing;
  const form = listingFormFor(l.assoc);
  const canSend = l.transferStatus === "draft" && !!(l.seller?.name?.trim());
  return (
    <div className="listing-card">
      <div className="thumb"/>
      <div style={{display: "flex", flexDirection: "column", gap: 8}}>
        <div style={{display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap"}}>
          <span className="ep-caption" style={{color: "var(--ep-fg-muted)"}}>MLS #{l.mls}</span>
          <StatusPill status={l.transferStatus}/>
          <span style={{fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600, padding: "3px 8px", borderRadius: 2, background: "#FFFAF0", border: "1px solid var(--ep-border)", color: "var(--ep-fg-muted)", display: "inline-flex", alignItems: "center", gap: 5}}>
            <IcDoc size={9}/> {form.short} form
          </span>
        </div>
        <div style={{font: "300 24px/1.1 var(--ep-font-display)", letterSpacing: "-0.01em"}}>{l.addr}</div>
        <div style={{display: "flex", gap: 16, color: "var(--ep-fg-muted)", fontSize: 13, flexWrap: "wrap"}}>
          <span>{l.city}</span>
          <span style={{display: "flex", alignItems: "center", gap: 4}}><IcBed/> {l.beds} bd</span>
          <span style={{display: "flex", alignItems: "center", gap: 4}}><IcBath/> {l.baths} ba</span>
          {l.sqft && <span style={{display: "flex", alignItems: "center", gap: 4}}><IcSq/> {l.sqft}</span>}
          <span style={{fontWeight: 600, color: "var(--ep-origin)"}}>{l.price}</span>
        </div>
        <div style={{fontSize: 13, color: "var(--ep-fg-muted)", marginTop: 4}}>
          Seller: <strong style={{color: "var(--ep-origin)", fontWeight: 600}}>
            {l.seller?.name || "— not matched —"}
          </strong>
          {l.seller2?.name && <> · {l.seller2.name}</>}
          {!l.seller?.name && <span style={{color: "var(--ep-rosewood)", marginLeft: 6}}>· Review before sending</span>}
        </div>
      </div>
      <div style={{display: "flex", flexDirection: "column", gap: 8}}>
        <button className="ep-btn secondary sm" onClick={onEdit}><IcEdit/> Review & Edit</button>
        <button className="ep-btn primary sm" onClick={onEdit} disabled={!canSend && l.transferStatus === "draft"}>
          <IcSend/> Send for Signature
        </button>
      </div>
    </div>
  );
}

function ListingDetailModal({ listing, onClose, onSave, onSend, agent }) {
  const [l, setL] = _uS(listing);
  const form = listingFormFor(l.assoc);
  const upd = (patch) => setL({...l, ...patch});
  const updSeller = (patch) => setL({...l, seller: {...l.seller, ...patch}});
  const hasBTSeller2 = !!(listing.seller2?.name || listing.seller2?.email);
  const [includeSeller2, setIncludeSeller2] = _uS(hasBTSeller2);

  // Seller name is required; email/phone can be filled in BoldSign if missing.
  const canSend = !!(l.seller?.name?.trim());

  return (
    <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && onClose()}>
      <div style={{width: "min(960px, 94vw)", maxHeight: "92vh", background: "var(--ep-bg-elevated)", borderRadius: 4, display: "flex", flexDirection: "column", animation: "slideUp 260ms var(--ep-ease)"}}>
        {/* Header */}
        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 28px", borderBottom: "1px solid var(--ep-border)"}}>
          <div>
            <div className="ep-caption" style={{color: "var(--ep-sunset)"}}>{form.short} · Listing Transfer Form</div>
            <div style={{font: "300 24px/1.1 var(--ep-font-display)", marginTop: 4}}>{l.addr}</div>
          </div>
          <button className="ep-btn ghost sm" onClick={onClose}><IcX/></button>
        </div>

        {/* Body */}
        <div className="scroll-y ep-stack-mobile" style={{padding: 28, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28}}>
          {/* Left: MLS data */}
          <div>
            <div className="ep-eyebrow" style={{color: "var(--ep-down-to-earth)"}}>Pulled from ARMLS</div>
            <div style={{padding: 20, background: "rgba(204,207,129,0.18)", border: "1px solid var(--ep-avocado)", borderRadius: 2, marginTop: 8}}>
              <DataRow label="MLS #" value={l.mls}/>
              <DataRow label="Address" value={l.addr}/>
              <DataRow label="City / ZIP" value={l.city}/>
              <DataRow label="List Price" value={l.price}/>
              <DataRow label="Beds / Baths" value={`${l.beds} / ${l.baths}`}/>
              <DataRow label="Sqft" value={l.sqft}/>
              <DataRow label="Status" value={l.status}/>
              <DataRow label="Days on Market" value={l.dom}/>
              <DataRow label="Current Broker" value={agent?.currentBroker || AGENT.currentBroker} last/>
            </div>
            <div style={{fontSize: 11, color: "var(--ep-fg-muted)", marginTop: 10, letterSpacing: "0.05em", textTransform: "uppercase"}}>
              Read-only · Synced from ARMLS
            </div>
          </div>

          {/* Right: Seller + edits */}
          <div>
            <div style={{display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8}}>
              <div className="ep-eyebrow" style={{color: "var(--ep-sunset)"}}>
                {l.sellerSource === "boldtrail" ? "Seller — From BoldTrail" : "Seller — From MLS"}
              </div>
            </div>
            {!l.seller?.name && (
              <div style={{padding: "10px 14px", background: "rgba(255,157,58,0.15)", border: "1px solid var(--ep-high-noon)", borderRadius: 2, fontSize: 12, color: "var(--ep-origin)", marginBottom: 16}}>
                Seller not found in BoldTrail. Enter the seller's details manually before sending.
              </div>
            )}
            <label className="ep-field">
              <span className="ep-eyebrow">Seller 1 Name</span>
              <input className="ep-input" value={l.seller?.name || ""} onChange={e => updSeller({name: e.target.value})}/>
            </label>
            <label className="ep-field">
              <span className="ep-eyebrow">Seller 1 Email</span>
              <input className="ep-input" value={l.seller?.email || ""} onChange={e => updSeller({email: e.target.value})} placeholder="seller@email.com"/>
            </label>
            <label className="ep-field">
              <span className="ep-eyebrow">Seller 1 Phone</span>
              <input className="ep-input" value={l.seller?.phone || ""} onChange={e => updSeller({phone: e.target.value})}/>
            </label>
            <label className="ep-field" style={{flexDirection: "row", alignItems: "center", gap: 8, cursor: "pointer"}}>
              <input type="checkbox" checked={includeSeller2} onChange={e => setIncludeSeller2(e.target.checked)} style={{accentColor: "var(--ep-sunset)"}}/>
              <span style={{fontSize: 13, color: "var(--ep-fg-muted)"}}>
                Include Seller 2
                {hasBTSeller2 && <span style={{fontSize: 11, marginLeft: 6, color: "var(--ep-saguaro, #2f6b43)"}}>· from BoldTrail</span>}
              </span>
            </label>
            {includeSeller2 && (
              <>
                <label className="ep-field">
                  <span className="ep-eyebrow">Seller 2 Name</span>
                  <input className="ep-input" value={l.seller2?.name || ""} onChange={e => setL({...l, seller2: {...(l.seller2||{}), name: e.target.value}})}/>
                </label>
                <label className="ep-field">
                  <span className="ep-eyebrow">Seller 2 Email</span>
                  <input className="ep-input" value={l.seller2?.email || ""} onChange={e => setL({...l, seller2: {...(l.seller2||{}), email: e.target.value}})} placeholder="seller2@email.com"/>
                </label>
              </>
            )}

            <div style={{marginTop: 20}}>
              <label className="ep-field">
                <span className="ep-eyebrow">REALTOR® Association</span>
                <select className="ep-input" value={l.assoc || "weserv"}
                  onChange={e => setL({...l, assoc: e.target.value})}>
                  <option value="weserv">WeSERV — West & SouthEast Valley</option>
                  <option value="phx">PAR — Phoenix REALTORS®</option>
                  <option value="saar">SAAR — Scottsdale Area</option>
                  <option value="tar">TAR — Tucson Association</option>
                  <option value="naar">NAAR — Northern Arizona</option>
                  <option value="paar">PAAR — Prescott Area</option>
                </select>
              </label>
            </div>

            <div style={{marginTop: 20}}>
              <div className="ep-eyebrow">Association form</div>
              <div style={{padding: 16, background: "#FFFAF0", border: "1px solid var(--ep-border)", borderRadius: 2, fontSize: 13, color: "var(--ep-fg)"}}>
                <div style={{display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 8}}>
                  <strong style={{color: "var(--ep-origin)"}}>{form.name}</strong>
                  <a href={form.url} target="_blank" rel="noreferrer" className="ep-link" style={{fontSize: 12, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4}}>
                    <IcExternal size={11}/> Official PDF
                  </a>
                </div>
                <div style={{color: "var(--ep-fg-muted)"}}>{form.formTitle}</div>
                <div style={{color: "var(--ep-fg-muted)", fontSize: 12, marginTop: 6}}>Submit to: {form.submitTo}</div>
                {form.note && <div style={{fontSize: 11.5, color: "var(--ep-fg-muted)", marginTop: 8, lineHeight: 1.5, fontStyle: "italic"}}>{form.note}</div>}
              </div>
            </div>

            <div style={{marginTop: 20}}>
              <div className="ep-eyebrow">Listing Parties</div>
              <div style={{padding: 16, background: "#FFFAF0", border: "1px solid var(--ep-border)", borderRadius: 2, fontSize: 13, color: "var(--ep-fg)"}}>
                <div style={{marginBottom: 8}}>Listing Agent: <strong>{LISTING_AGENT.name}</strong> <span style={{color: "var(--ep-fg-muted)"}}>(agent of record on all listings)</span></div>
                <div style={{marginBottom: 8}}>Co-List Agent: <strong>{agent?.name || "—"}</strong> <span style={{color: "var(--ep-fg-muted)"}}>· ADRE #{agent?.license || "—"}</span></div>
                <div style={{paddingTop: 8, borderTop: "1px solid var(--ep-divider)", color: "var(--ep-fg-muted)"}}>
                  Transferred from <strong style={{color: "var(--ep-origin)"}}>{TRANSFERRING_BROKERAGE}</strong> → received by <strong style={{color: "var(--ep-origin)"}}>{RECEIVING_BROKERAGE}</strong>
                </div>
              </div>
            </div>

            <div style={{marginTop: 20}}>
              <div className="ep-eyebrow">Routing</div>
              <div style={{padding: 16, background: "#FFFAF0", border: "1px solid var(--ep-border)", borderRadius: 2, fontSize: 13, color: "var(--ep-fg)"}}>
                <div style={{marginBottom: 8}}><strong>1.</strong> Sent to <strong>{l.seller.name || "owner"}</strong> for e-signature</div>
                <div style={{marginBottom: 8}}><strong>2.</strong> Sent to <strong>{TRANSFERRING_BROKERAGE}</strong> as original listing broker of record</div>
                <div><strong>3.</strong> Countersigned by <strong>Amber Valentine</strong>, {RECEIVING_BROKERAGE}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{display: "flex", justifyContent: "space-between", padding: "18px 28px", borderTop: "1px solid var(--ep-border)", background: "#FFFAF0"}}>
          <button className="ep-btn ghost" onClick={onClose}>Cancel</button>
          <div className="hstack">
            <button className="ep-btn secondary sm" onClick={() => onSave({...l, seller2: includeSeller2 ? l.seller2 : undefined})}>Save as Draft</button>
            <button className="ep-btn primary sm" onClick={() => onSend({...l, seller2: includeSeller2 ? l.seller2 : undefined})} disabled={!canSend}>
              <IcSend/> Send for Signature
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DataRow({ label, value, last }) {
  return (
    <div style={{display: "grid", gridTemplateColumns: "120px 1fr", gap: 16, padding: "8px 0", borderBottom: last ? 0 : "1px solid rgba(71,60,22,0.1)", fontSize: 13}}>
      <div style={{color: "var(--ep-fg-muted)", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600}}>{label}</div>
      <div style={{color: "var(--ep-origin)"}}>{value}</div>
    </div>
  );
}

// ============ ADRE ============
// Step 04/09 — happens right after agreements, before association transfers.
// The agent copies their info, follows the numbered steps on ptl.az.gov, then
// confirms submission here before continuing.
function StepADRE({ onNext, onBack, agent, gate, updateTracker , eyebrow}) {
  const [confirmed, setConfirmed] = _uS(false);
  const [saving, setSaving] = _uS(false);

  async function handleConfirm() {
    setSaving(true);
    try { await updateTracker?.({ adreConfirmed: true }); } catch { /* non-blocking */ }
    finally { setSaving(false); }
    onNext();
  }

  const steps = [
    { n: "01", t: "Log in to ADRE Online Services",      d: "Visit ptl.az.gov and sign in with your existing account." },
    { n: "02", t: 'Select "Add or Change My Employer"',  d: "Under the Salesperson section, choose this option to initiate a broker transfer." },
    { n: "03", t: "Enter the new broker information",    d: "Use the copy-ready details on the left — brokerage name, designated broker, and office address." },
    { n: "04", t: "Submit and return here",              d: "Once submitted, check the confirmation box below and continue your onboarding." },
  ];

  return (
    <div style={{maxWidth: 1100}}>
      <StepHeader eyebrow={eyebrow || "Step 04 / 09"} title="Transfer your license to EveryPlace."
        subtitle="Complete your change of employing broker on the ADRE online portal. We've prepared everything you need."/>

      <div className="ep-stack-mobile" style={{display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 32, marginBottom: 32}}>
        {/* Copy-ready block */}
        <div className="ep-card" style={{padding: 28}}>
          <div className="ep-eyebrow" style={{color: "var(--ep-sunset)"}}>Copy-ready info</div>
          <h3 style={{font: "300 28px/1.15 var(--ep-font-display)", margin: "8px 0 16px"}}>Paste these into the ADRE form.</h3>
          <div className="vstack" style={{gap: 0}}>
            <CopyRow label="Your License #"        value={agent.license || "—"}/>
            <CopyRow label="New Broker of Record"  value="EveryPlace"/>
            <CopyRow label="Designated Broker"     value="Amber Valentine"/>
            <CopyRow label="Brokerage License #"   value="LC722428000"/>
            <CopyRow label="Office Address"        value="8631 W Union Hills Dr, Ste 206, Peoria, AZ 85382" last/>
          </div>
        </div>

        {/* Step-by-step instructions */}
        <div style={{display: "flex", flexDirection: "column", gap: 10}}>
          <div className="ep-eyebrow" style={{color: "var(--ep-sunset)"}}>Step-by-step</div>
          {steps.map(s => (
            <div key={s.n} style={{display: "flex", gap: 14, padding: "13px 16px",
              background: "var(--ep-bg-elevated)", border: "1px solid var(--ep-border)", borderRadius: 2}}>
              <div style={{font: "300 18px/1 var(--ep-font-display)", color: "var(--ep-sunset)", flexShrink: 0, minWidth: 24}}>{s.n}</div>
              <div>
                <div style={{font: "500 13px/1.25 var(--ep-font-body)", color: "var(--ep-origin)", marginBottom: 3}}>{s.t}</div>
                <div style={{fontSize: 12, color: "var(--ep-fg-muted)", lineHeight: 1.5}}>{s.d}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Portal button */}
      <div style={{padding: 32, textAlign: "center", border: "1px dashed var(--ep-border-strong)", background: "#FFFAF0", borderRadius: 2, marginBottom: 28}}>
        <IcShield size={32}/>
        <h3 style={{font: "300 28px/1.15 var(--ep-font-display)", margin: "14px 0 8px"}}>Ready to transfer?</h3>
        <p style={{color: "var(--ep-fg-muted)", maxWidth: "50ch", margin: "0 auto 20px", fontSize: 14}}>
          The ADRE portal is where the official transfer happens. Use the copy-ready details above. Most transfers process in 5–7 business days — you'll remain licensed with no gap.
        </p>
        <a href="https://ptl.az.gov/dre/#/login" target="_blank" rel="noreferrer" style={{textDecoration: "none"}}>
          <button className="ep-btn primary">Open ADRE Online Portal <IcExternal/></button>
        </a>
      </div>

      {/* MLS-OBSERVED license state.
          ADRE has no API, so the checkbox below is the agent's own word. The MLS
          shows the same fact independently: a license change moves the member's
          office, which our twice-daily enrichment picks up.

          Only the POSITIVE state is shown. An agent who submitted an hour ago has
          not appeared in the MLS yet — our sync runs twice a day — so rendering
          "not transferred" would contradict something they just did and generate
          a support ticket. The not-yet and unknown states are what Agent Services
          needs, and they live on the admin pipeline instead. */}
      {gate?.licenseMoved === true && (
        <div style={{display: "flex", alignItems: "flex-start", gap: 12, padding: "16px 20px",
          background: "rgba(204,207,129,0.18)", border: "1px solid var(--ep-avocado)",
          borderRadius: 2, marginBottom: 16}}>
          <IcCheck size={16}/>
          <div>
            <div style={{font: "500 14px/1.3 var(--ep-font-body)", color: "var(--ep-origin)", marginBottom: 3}}>
              Your license transfer has come through.
            </div>
            <div style={{fontSize: 13, color: "var(--ep-fg-muted)"}}>
              The MLS now lists you with EveryPlace, so nothing further is needed here.
            </div>
          </div>
        </div>
      )}

      {/* Confirmation checkbox */}
      <label style={{display: "flex", alignItems: "flex-start", gap: 12, padding: "20px 24px",
        background: confirmed ? "rgba(204,207,129,0.18)" : "var(--ep-bg-elevated)",
        border: `1px solid ${confirmed ? "var(--ep-avocado)" : "var(--ep-border)"}`,
        borderRadius: 2, cursor: "pointer", marginBottom: 32,
        transition: "background 160ms var(--ep-ease), border-color 160ms var(--ep-ease)"}}>
        <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)}
          style={{marginTop: 2, accentColor: "var(--ep-sunset)", width: 18, height: 18, flexShrink: 0}}/>
        <div>
          <div style={{font: "500 15px/1.25 var(--ep-font-body)", color: "var(--ep-origin)", marginBottom: 4}}>
            I have submitted my license transfer to ADRE.
          </div>
          <div style={{fontSize: 13, color: "var(--ep-fg-muted)"}}>
            I understand the transfer processes within 5–7 business days and I will remain actively licensed throughout.
          </div>
        </div>
      </label>

      <FooterNav onBack={onBack} onNext={handleConfirm} nextDisabled={!confirmed || saving}
        nextLabel={saving ? "Saving…" : confirmed ? "Continue to Association Transfers" : "Confirm ADRE submission to continue"}/>
    </div>
  );
}

function CopyRow({ label, value, last }) {
  const [copied, setCopied] = _uS(false);
  const copy = () => { navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1400); };
  return (
    <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderBottom: last ? 0 : "1px solid var(--ep-divider)", gap: 16}}>
      <div>
        <div className="ep-caption" style={{color: "var(--ep-fg-muted)"}}>{label}</div>
        <div style={{fontSize: 15, color: "var(--ep-origin)", fontWeight: 500, marginTop: 3}}>{value}</div>
      </div>
      <button className="ep-btn ghost sm" onClick={copy} style={{flexShrink: 0}}>
        {copied ? <><IcCheck size={12}/> Copied</> : "Copy"}
      </button>
    </div>
  );
}

export { StepListings, StepADRE };
